import type { JSX, ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, replay, type Replay } from '../api';

/**
 * Undo for changes of values: every edit that knows how to reverse itself
 * records that step; the button in the header or Ctrl+Z / Cmd+Z takes back
 * the latest one. Inside a text field the browser's own undo stays in charge
 * - that is where one expects it. Deletions are not undone here; they ask
 * before they happen.
 *
 * Only the own steps: the list lives in this browser tab (sessionStorage, per
 * account), survives reloading the page and is gone with the tab. Each step
 * carries `expect` - the values the own change set - and the server writes
 * the old values back only while the fields still hold them (core/undo on
 * the server). Has someone else changed them in between, the step is refused
 * and dropped; another person's work is never taken back.
 *
 * After an undo every query is refetched, so all views that show the value -
 * a project page and the cross-project overviews alike - agree again.
 */
export interface UndoStep extends Replay {
  /** What is undone, e.g. "Kennwert KW_330". Shown on the button. */
  label: string;
}

interface UndoApi {
  record: (step: UndoStep | null) => void;
  /**
   * Records the step once `write` has succeeded - a failed write leaves
   * nothing to undo. A function gets the server's answer, for changes whose
   * stored result the server works out (a finish date becomes a duration).
   */
  recordAfter: <T>(write: Promise<T>, step: UndoStep | null | ((result: T) => UndoStep | null)) => Promise<T>;
  /** Forget every step - after a restore they would act on a state that is gone. */
  clear: () => void;
  undo: () => Promise<void>;
  last: UndoStep | null;
  busy: boolean;
  error: string | null;
}

const UndoContext = createContext<UndoApi | null>(null);
export const UNDO_LIMIT = 50;

/**
 * The step that writes `back` (the old values) while the fields still hold
 * what `forward` set. Null when there is nothing to take back.
 */
export function guardedStep(label: string, method: Replay['method'], path: string, back: Record<string, unknown>, forward: Record<string, unknown>): UndoStep | null {
  const keys = Object.keys(back);
  if (keys.length === 0) return null;
  const expect = Object.fromEntries(keys.map((k) => [k, forward[k] ?? null]));
  return { label, method, path, body: { ...back, expect } };
}

/** The steps kept in storage; anything that does not look like a step is dropped. */
export function parseSteps(raw: string | null): UndoStep[] {
  if (!raw) return [];
  try {
    const list: unknown = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.filter((s): s is UndoStep =>
      typeof s === 'object' && s !== null
      && typeof (s as UndoStep).label === 'string'
      && ['PATCH', 'PUT', 'POST'].includes((s as UndoStep).method)
      && typeof (s as UndoStep).path === 'string' && (s as UndoStep).path.startsWith('/')
      && typeof (s as UndoStep).body === 'object' && (s as UndoStep).body !== null).slice(-UNDO_LIMIT);
  } catch {
    return [];
  }
}

/** Appends a step and keeps the newest UNDO_LIMIT. */
export function pushStep(stack: readonly UndoStep[], step: UndoStep): UndoStep[] {
  return [...stack.slice(-(UNDO_LIMIT - 1)), step];
}

const storageKey = (userId: string): string => `pst.undo.${userId}`;

function load(userId: string | undefined): UndoStep[] {
  if (!userId) return [];
  try { return parseSteps(sessionStorage.getItem(storageKey(userId))); } catch { return []; }
}

function save(userId: string | undefined, stack: readonly UndoStep[]): void {
  if (!userId) return;
  try {
    if (stack.length === 0) sessionStorage.removeItem(storageKey(userId));
    else sessionStorage.setItem(storageKey(userId), JSON.stringify(stack));
  } catch {
    // Storage blocked or full: undo still works until the page is reloaded.
  }
}

/** True when the key goes to a field that has its own undo. */
function inEditableField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function UndoProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  // Shares the cache entry of App's own query; signed out it simply has no user.
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false });
  const userId = me.data?.id;
  const [stack, setStack] = useState<UndoStep[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // While an undo runs, its own writes must not land on the stack again.
  const replaying = useRef(false);
  const stackRef = useRef(stack);
  stackRef.current = stack;
  const userRef = useRef(userId);
  userRef.current = userId;

  // Another account in this tab gets its own list, never the previous one's.
  useEffect(() => { setStack(load(userId)); setError(null); }, [userId]);

  const change = useCallback((next: (s: UndoStep[]) => UndoStep[]) => {
    setStack((s) => { const n = next(s); save(userRef.current, n); return n; });
  }, []);

  const record = useCallback((step: UndoStep | null) => {
    if (replaying.current || !step) return;
    setError(null);
    change((s) => pushStep(s, step));
  }, [change]);

  const recordAfter = useCallback(<T,>(write: Promise<T>, step: UndoStep | null | ((result: T) => UndoStep | null)): Promise<T> =>
    write.then((result) => { record(typeof step === 'function' ? step(result) : step); return result; }), [record]);

  const clear = useCallback(() => { change(() => []); setError(null); }, [change]);

  const undo = useCallback(async () => {
    const step = stackRef.current[stackRef.current.length - 1];
    if (!step || busy) return;
    setBusy(true);
    replaying.current = true;
    try {
      await replay(step);
      setError(null);
    } catch (e) {
      setError(`„${step.label}“ ließ sich nicht rückgängig machen: ${(e as Error).message}`);
    } finally {
      // Done or refused, the step is used up: a refused one would be refused again.
      change((s) => s.slice(0, -1));
      replaying.current = false;
      setBusy(false);
      await queryClient.invalidateQueries();
    }
  }, [busy, change, queryClient]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'z' || event.shiftKey || !(event.ctrlKey || event.metaKey)) return;
      if (inEditableField(event.target)) return;
      event.preventDefault();
      void undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo]);

  const value = useMemo(() => ({ record, recordAfter, clear, undo, last: stack[stack.length - 1] ?? null, busy, error }), [record, recordAfter, clear, undo, stack, busy, error]);
  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>;
}

/** Recording outside a provider (e.g. in a test) does nothing. */
export function useUndo(): UndoApi {
  return useContext(UndoContext) ?? { record: () => undefined, recordAfter: (write) => write, clear: () => undefined, undo: async () => undefined, last: null, busy: false, error: null };
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function UndoButton(): JSX.Element {
  const { undo, last, busy, error } = useUndo();
  const keys = isMac ? '⌘Z' : 'Strg+Z';
  return (
    <Tooltip label={error ?? (last ? `Rückgängig: ${last.label} (${keys})` : 'Nichts rückgängig zu machen')} color={error ? 'red' : undefined} multiline maw={320}>
      <ActionIcon variant="subtle" color={error ? 'red' : 'gray'} size="lg" aria-label="Rückgängig" disabled={!last && !error} loading={busy} onClick={() => void undo()}>
        ↶
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * The reverse of a patch: for each changed key, the value it had before.
 * `previous` maps keys to their old values; keys it does not know are left
 * out, so an undo never writes something it did not see.
 */
export function inversePatch<T extends Record<string, unknown>>(patch: T, previous: Record<string, unknown>): Partial<T> {
  const out: Partial<T> = {};
  for (const key of Object.keys(patch) as (keyof T)[]) {
    if (key in previous) out[key] = previous[key as string] as T[keyof T];
  }
  return out;
}
